import sentry from '@/3rd-parties/sentry'
import LeaderboardApi from '@/api/leaderboard'
import { isFeatureEnabled, getVariant } from './feature-toggles.module'

const LEADERBOARD_SLIDE_TOP_N = 20
const LEADERBOARD_SLIDE_AROUND_K = 10

// Merge top-N with the around-window so the audience's own row survives even below the cut.
const mergeLeaderboards = (rowsKey, idKey, boards) => {
  const seen = new Set()
  const rows = boards
    .flatMap((board) => (board && board[rowsKey]) || [])
    .filter((row) => {
      const id = String(row[idKey])
      if (seen.has(id)) {
        return false
      }
      seen.add(id)
      return true
    })
    .sort((a, b) => a.index - b.index)
  // top-N and the around-window aren't contiguous; flag the gap so the render shows a separator.
  rows.forEach((row, i) => {
    const next = rows[i + 1]
    row.breakPoint = Boolean(next && Number(next.index) > Number(row.index) + 1)
  })
  return { [rowsKey]: rows }
}

const inFlightRequests = {}

// Share one request among overlapping identical calls (a reload's staged re-mount
// fires the same fetch twice); a later call re-runs once the in-flight one settles.
const dedupeInFlight = (dedupKey, run) => {
  if (!inFlightRequests[dedupKey]) {
    inFlightRequests[dedupKey] = run().finally(() => { delete inFlightRequests[dedupKey] })
  }
  return inFlightRequests[dedupKey]
}

const _state = {
  isLeaderboardVisible: false,
}

const MUTATION_TYPES = {
  TOGGLE_LEADERBOARD_VISIBILITY: 'TOGGLE_LEADERBOARD_VISIBILITY',
}

const UNLEASH_FLAG_NEW_LEADERBOARD = 'AHA-42758-new-leaderboard'

const _getters = {
  quizSlideIdsUpTo: (_, __, rootState, rootGetters) => (order) => (rootState.slides || [])
    .filter((slide) => rootGetters.isQuizSlide(slide) && (order === undefined || slide.order <= order))
    .sort((a, b) => a.order - b.order)
    .map((slide) => Number(slide.id)),

  canUseNewLeaderboard(_, __, rootState, rootGetters) {
    if (!isFeatureEnabled(rootGetters, UNLEASH_FLAG_NEW_LEADERBOARD)) return false
    const turnOnDate = getVariant(rootGetters, UNLEASH_FLAG_NEW_LEADERBOARD)?.payload?.value
    const { presentation, slides } = rootState
    if (!turnOnDate || !presentation) return false
    // gate by last reset (if available) or createdAt
    const resetTimestamp = Number(presentation.resetTimeStamp)
    const boundary = Number.isFinite(resetTimestamp) && resetTimestamp > 0 ? resetTimestamp : presentation.createdAt
    if (!boundary) return false
    if (new Date(boundary) <= new Date(turnOnDate)) return false
    // gate to decks that actually contain a plugin quiz slide
    return (slides || []).some((slide) => rootGetters['embedApp/isScoredSlide'](slide))
  },

  isLeaderboardVisible(state, getters) {
    if (!getters.canUseNewLeaderboard) return false
    return state.isLeaderboardVisible
  },
}

const actions = {
  // show/hide the leaderboard during the quiz
  subscribeLeaderboardVisibilityAction: async ({ getters, dispatch }, { presentation }) => {
    if (!getters.canUseNewLeaderboard) return
    dispatch('kvSync/subscribeKeyAction', {
      key: `/leaderboards/${presentation.id}`,
    }, { root: true })
  },

  fetchLeaderboardSlideSnapshotAction: (_ctx, { key, t, subject }) => {
    return dedupeInFlight(`snapshot-${key}-${t}`, () => LeaderboardApi.getLeaderboardSlideSnapshot({ key, t, subject }))
  },

  // Self-paced: no presenter, so read the board live (top-N + around-window, merged below).
  fetchLeaderboardSlideLiveAction: async ({ rootState, getters }, { subject, subjectId, order }) => {
    const rowsKey = subject === 'team' ? 'teams' : 'audiences'
    const idKey = subject === 'team' ? 'id' : 'audience'
    const slideIds = getters.quizSlideIdsUpTo(order)
    if (!slideIds.length) {
      return { [rowsKey]: [] }
    }
    const base = {
      presentationId: rootState.presentation.id,
      presentationVersion: rootState.presentation.version,
      subject,
      teamScoringRule: rootState.presentation.teamScoringRule,
      slideIds,
    }
    const [top, around] = await Promise.all([
      LeaderboardApi.getLeaderboardSlideTopN({ ...base, n: LEADERBOARD_SLIDE_TOP_N }),
      subjectId
        ? LeaderboardApi.getLeaderboardSlideAround({ ...base, subjectId: String(subjectId), k: LEADERBOARD_SLIDE_AROUND_K })
          .catch((err) => { sentry.captureException(err); return null })
        : Promise.resolve(null),
    ])
    return mergeLeaderboards(rowsKey, idKey, [top, around])
  },

  fetchScoreAction: async ({ rootState }, {
    subject, subjectId, slideIds, aggregation,
  }) => {
    const { presentation } = rootState
    return LeaderboardApi.getScore({
      presentationId: presentation.id,
      presentationVersion: presentation.version,
      subject,
      subjectId,
      slideIds,
      aggregation,
      teamScoringRule: presentation.teamScoringRule,
    })
  },

  fetchStatsAction: ({ rootState }, { subject, subjectId, slideIds }) => {
    const { presentation } = rootState
    const dedupKey = `stats-${presentation.id}-${presentation.version}-${subject}-${subjectId}-${slideIds}`
    return dedupeInFlight(dedupKey, () => LeaderboardApi.getStats({
      presentationId: presentation.id,
      presentationVersion: presentation.version,
      subject,
      subjectId,
      slideIds,
    }).catch((err) => {
      sentry.captureException(err)
      return null
    }))
  },
}

const mutations = {
  [MUTATION_TYPES.TOGGLE_LEADERBOARD_VISIBILITY](state, isVisible) {
    if (typeof isVisible !== 'boolean') {
      state.isLeaderboardVisible = !state.isLeaderboardVisible
      return
    }
    state.isLeaderboardVisible = isVisible
  },
}

const plugins = {
  updateLeaderboardVisibility: (store) => {
    store.subscribeAction((action) => {
      if (action.type === 'kvSync/updateKeyValueAction') {
        const { key, value } = action.payload
        if (key.includes('/leaderboards/')) {
          store.commit(`leaderboard/${MUTATION_TYPES.TOGGLE_LEADERBOARD_VISIBILITY}`, JSON.parse(value).isVisible)
        }
      }
    })
  },
}

const leaderboardModule = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
  plugins,
}

export default leaderboardModule
